import { Box } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { ObjectsEnum } from "..";
import { useAppStore } from "../../../../../../../hooks/useStores";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import { useEditor } from "../../../../../../hooks/useEditor";
import { useObjects } from "../../../../../../hooks/useObjects";
import useSmartImageHeader from "../../../../../../hooks/useSmartImageHeader";
import type { ILayer } from "../../../../../../types";
import { findGroupByElementId } from "../../../../utils/smartImageUtils";
import DraggableList from "./DraggableList/DraggableList";
import styles from "./Layers.module.scss";

export default function Layers() {
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.layers" });
	const { campaignStore } = useAppStore();
	const editor = useEditor();
	const objects = useObjects() as ILayer[];
	const [layerObjects, setLayerObjects] = React.useState<ILayer[]>([]);
	const { handleDeleteSmartImage } = useSmartImageHeader();

	React.useEffect(() => {
		if (objects) {
			setLayerObjects(objects);
		}
	}, [objects]);

	React.useEffect(() => {
		const watcher = async () => {
			if (objects) {
				setLayerObjects(objects);
			}
		};
		if (editor) {
			editor.on("history:changed", watcher);
		}
		return () => {
			if (editor) {
				editor.off("history:changed", watcher);
			}
		};
	}, [editor, objects, layerObjects]);

	const handleSelect = (id: string) => {
		editor?.objects.select(id);
	};

	const handleToggleLock = (id: string, locked: boolean) => {
		if (locked) {
			editor?.objects.unlock(id);
		} else {
			editor?.objects.lock(id);
		}
	};

	const handleToggleVisibility = (id: string, visible?: boolean) => {
		editor?.objects.update({ visible: !visible }, id);
	};

	const handleRemove = (id: string) => {
		if (id === ObjectsEnum.InnerRectangle || id === ObjectsEnum.OuterRectangle) {
			handleDeleteSmartImage();
		}

		// Check if element is inside a group
		if (editor?.canvas?.canvas) {
			const parentGroup = findGroupByElementId(editor.canvas.canvas, id);

			if (parentGroup && parentGroup._objects) {
				// Filter out inner/outer rectangles when counting
				const isInnerOrOuterRectangle = (objId: string) =>
					objId === ObjectsEnum.InnerRectangle || objId === ObjectsEnum.OuterRectangle;

				// Find the object to remove
				const objectToRemove = parentGroup._objects.find((obj: any) => obj.id === id);

				if (objectToRemove) {
					// Remove the object from the group's _objects array
					const index = parentGroup._objects.indexOf(objectToRemove);
					if (index > -1) {
						parentGroup._objects.splice(index, 1);
					}

					// Mark the group as dirty and render
					parentGroup.dirty = true;
					parentGroup.setCoords();
					editor.canvas.canvas.requestRenderAll();

					// After removal, check how many non-rectangle objects remain
					const remainingNonRectObjects = parentGroup._objects.filter(
						(obj: any) => !isInnerOrOuterRectangle(obj.id),
					);

					// Case 1: If only 1 non-rectangle object remains, ungroup and select it
					if (remainingNonRectObjects.length === 1) {
						// Select the group first
						editor.canvas.canvas.setActiveObject(parentGroup);
						// Ungroup
						editor.objects.ungroup();
						// After ungrouping, select the remaining object
						setTimeout(() => {
							const remainingObject = editor.canvas.canvas
								.getObjects()
								.find((obj: any) => obj.id === remainingNonRectObjects[0].id);
							if (remainingObject) {
								// editor.canvas.canvas.setActiveObject(remainingObject);
								// editor.canvas.canvas.requestRenderAll();
								editor.canvas.canvas.discardActiveObject();
								editor.canvas.canvas.setActiveObject(remainingObject);
								editor.canvas.canvas.renderAll();
							}
						}, 50);
					}
					// Case 2: If no objects remain, remove the empty group
					else if (parentGroup._objects.length === 0) {
						editor.canvas.canvas.remove(parentGroup);
						editor.canvas.canvas.discardActiveObject();
						editor.canvas.canvas.requestRenderAll();
						editor.history.save();
					}
					// Case 3: If 2 or more non-rectangle objects remain, select the group
					else if (remainingNonRectObjects.length >= 2) {
						editor.canvas.canvas.discardActiveObject();
						editor.canvas.canvas.setActiveObject(parentGroup);
						editor.canvas.canvas.renderAll();
						// editor.canvas.canvas.setActiveObject(parentGroup);
						// editor.canvas.canvas.requestRenderAll();
						editor.history.save();
					}

					// Force update of layers list
					setTimeout(() => {
						const currentObjects = editor.canvas.canvas
							.getObjects()
							.filter((o: any) => o.type !== "Frame" && o.type !== "Background") as ILayer[];
						setLayerObjects(currentObjects);
					}, 100);

					return;
				}
			}
		}

		// If not in a group, use normal remove
		editor?.objects.remove(id);
	};

	const handleDragEnd = (newLayerObjects: ILayer[]) => {
		setLayerObjects(newLayerObjects);
		campaignStore.orderedLayers = newLayerObjects;
	};
	const reversedLayerObjects = [...layerObjects].reverse();
	return (
		<Box className={styles.layersTabContainer}>
			<MainTabHeader title={t("title")} />
			<Box className={styles.tabs}>
				<Box className={styles.fullWidth}>
					<DraggableList
						layerObjects={reversedLayerObjects}
						onSelect={handleSelect}
						onToggleLock={handleToggleLock}
						onToggleVisibility={handleToggleVisibility}
						onRemove={handleRemove}
						onDragEnd={handleDragEnd}
					/>
				</Box>
			</Box>
		</Box>
	);
}
